import React, { useCallback } from "react";
import { useServices } from "@core/servicesContext";
import { EditPostModal } from "@ui/components/post/editPostModal";
import { useModalTools } from "@ui/modals/modalContext";
import {
	AsyncResult,
	PaginatedDataResult,
	useMappedStore,
	useObservablePaginatedData,
	useStoredData,
} from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Media } from "@domain/media/media";
import { Profile } from "@domain/user/profile";
import { PostComment } from "@domain/post/comment/postComment";
import { Post, PostReactionType } from "./post";
import { FeedSource } from "./postService";

export function useFeed(userId?: string, source?: FeedSource, organizationId?: string): PaginatedDataResult<Post> {
	const { postService } = useServices();

	return useObservablePaginatedData(
		postService.getObservableFeed(userId, source),
		() => postService.fetchFeed(userId, source),
		() => postService.fetchMoreFeed(userId, source),
		[userId, source, organizationId]
	);
}

export function usePost(id: string): AsyncResult<Post> {
	const { postService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(id, postService.postStore, [currentProfile?.id]);
}

export function usePostComments(postId: string, organizationId?: string): PaginatedDataResult<PostComment> {
	const { postService } = useServices();
	return useMappedStore(postId, postService.postCommentPresentation, [postId, organizationId]);
}

export function usePostCreation(groupId?: string): (assets?: Media[]) => void {
	const { createModal } = useModalTools();

	return useCallback(
		(assets?: Media[]) => {
			createModal((close) => <EditPostModal close={close} groupId={groupId} initialAssets={assets} />, {
				closeOnBackdropClick: true,
			});
		},
		[groupId]
	);
}

export function useReactionProfiles(
	postId: string,
	reaction: PostReactionType,
	commentId: string | null = null
): PaginatedDataResult<Profile> {
	const { postService } = useServices();
	if (!commentId) return useMappedStore(postId, postService.getReactionStore(reaction), [postId, reaction]);
	else return useMappedStore(commentId, postService.getCommentsReaction(), [commentId, reaction]);
}

export function useCommentReplies(commentId: string, organizationId?: string): PaginatedDataResult<PostComment> {
	const { postService } = useServices();
	return useMappedStore(commentId, postService.commentRepliesPresentation, [commentId, organizationId]);
}
